import { useDropzone } from 'react-dropzone';
// @mui
import { Box, Typography, Button, Stack, LinearProgress, keyframes } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';

import { Iconify } from '../iconify';

// Keyframe animations
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const shimmerAnimation = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

// ----------------------------------------------------------------------

const StyledDropZone = styled('div')(({ theme }) => ({
  width: '100%',
  minHeight: 220,
  display: 'flex',
  cursor: 'pointer',
  overflow: 'hidden',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius * 3,
  border: `3px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.02),
  transition: theme.transitions.create(
    ['border-color', 'background-color', 'transform', 'box-shadow'],
    {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }
  ),
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  '&:active': {
    transform: 'translateY(0px)',
  },
}));

const StyledUploadButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 4,
  padding: theme.spacing(1.8, 4),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 700,
  fontSize: '0.95rem',
  textTransform: 'none',
  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  border: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: theme.transitions.create(['transform', 'box-shadow', 'background'], {
    duration: theme.transitions.duration.short,
  }),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.3
    )}, transparent)`,
    animation: `${shimmerAnimation} 2s ease-in-out infinite`,
    opacity: 0,
  },
  '&:hover': {
    transform: 'translateY(-3px) scale(1.02)',
    boxShadow: `0 12px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
    '&::before': {
      opacity: 1,
    },
  },
  '&:active': {
    transform: 'translateY(-1px) scale(0.98)',
  },
}));

const ImagePreviewBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 120,
  height: 120,
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  border: `2px solid ${alpha(theme.palette.grey[500], 0.24)}`,
  transition: theme.transitions.create(['border-color', 'transform'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'scale(1.02)',
  },
}));

const RemoveButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  minWidth: 28,
  width: 28,
  height: 28,
  padding: 0,
  borderRadius: '50%',
  backgroundColor: alpha(theme.palette.error.main, 0.88),
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.error.main,
    transform: 'scale(1.1)',
  },
}));

// ----------------------------------------------------------------------

interface UploadBoxProps {
  files: File[];
  onDrop: (acceptedFiles: File[]) => void;
  onRemove: (index: number) => void;
  multiple?: boolean;
  accept?: Record<string, string[]>;
  maxSize?: number;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  helperText?: string;
  placeholder?: string;
}

export default function UploadBox({
  files = [],
  onDrop,
  onRemove,
  multiple = true,
  accept = { 'image/*': [] },
  maxSize,
  disabled = false,
  loading = false,
  error,
  helperText,
  placeholder,
}: UploadBoxProps) {
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    multiple,
    disabled: disabled || loading,
    accept,
    maxSize,
    onDrop,
  });

  const hasFiles = files.length > 0;
  const isError = isDragReject || !!error;

  return (
    <Stack spacing={2}>
      <StyledDropZone
        {...getRootProps()}
        sx={{
          ...(isDragActive && {
            borderColor: 'primary.main',
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
            animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
            borderStyle: 'solid',
          }),
          ...(isError && {
            borderColor: 'error.main',
            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none',
          }),
        }}
      >
        <input
          {...getInputProps()}
          style={{
            position: 'absolute',
            opacity: 0,
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            zIndex: -1,
          }}
        />

        {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}

        <Stack spacing={3} alignItems="center" sx={{ p: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              transition: 'all 0.3s ease',
              '&::before': {
                content: '""',
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: (theme) =>
                  `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(
                    theme.palette.primary.light,
                    0.1
                  )})`,
                transform: isDragActive ? 'scale(1.2)' : 'scale(1)',
                transition: 'transform 0.3s ease',
              },
            }}
          >
            <Iconify
              icon={isDragActive ? 'eva:cloud-upload-fill' : 'eva:cloud-upload-outline'}
              width={40}
              sx={{
                color: 'primary.main',
                zIndex: 1,
                transform: isDragActive ? 'scale(1.1)' : 'scale(1)',
                transition: 'all 0.3s ease',
              }}
            />
          </Box>

          <Stack spacing={2} alignItems="center">
            <Typography
              variant="h5"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                textAlign: 'center',
                background: (theme) =>
                  `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {isDragActive ? 'Drop files here' : 'Upload Files'}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
                maxWidth: 300,
                lineHeight: 1.5,
              }}
            >
              {placeholder ?? 'Drag & drop files here, or click to select'}
            </Typography>

            <Box sx={{ mt: 1 }}>
              <StyledUploadButton variant="contained" disabled={disabled || loading}>
                <Iconify icon="eva:cloud-upload-outline" width={20} sx={{ mr: 1.5 }} />
                Choose Files
              </StyledUploadButton>
            </Box>
          </Stack>

          {helperText && (
            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              {helperText}
            </Typography>
          )}

          {error && (
            <Typography variant="caption" sx={{ color: 'error.main', textAlign: 'center' }}>
              {error}
            </Typography>
          )}
        </Stack>
      </StyledDropZone>

      {/* File Previews */}
      {hasFiles && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Selected Files ({files.length})
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 2,
            }}
          >
            {files.map((file, index) => {
              const isImage = file.type.startsWith('image/');
              const previewUrl = isImage ? URL.createObjectURL(file) : null;

              return (
                <ImagePreviewBox key={`${file.name}-${index}`}>
                  {isImage && previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={file.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onLoad={() => {
                        if (previewUrl) URL.revokeObjectURL(previewUrl);
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.12),
                      }}
                    >
                      <Iconify
                        icon="eva:file-outline"
                        width={32}
                        sx={{ color: 'text.secondary' }}
                      />
                    </Box>
                  )}

                  <RemoveButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(index);
                    }}
                  >
                    <Iconify icon="eva:close-outline" width={16} />
                  </RemoveButton>

                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: 0.5,
                      backgroundColor: (theme) => alpha(theme.palette.grey[900], 0.72),
                      color: 'white',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.7rem',
                        lineHeight: 1.2,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {file.name}
                    </Typography>
                  </Box>
                </ImagePreviewBox>
              );
            })}
          </Box>
        </Box>
      )}
    </Stack>
  );
}
